import { Info, Layers } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AccidentMark } from "@/features/accident";
import { SamePhoneRecords } from "@/features/same-phone";
import { SAME_EMAIL_CAVEAT, accidentSections } from "@/lib/accident";
import { contactName } from "@/lib/contacts";
import { railOffset } from "@/lib/phone-page";
import { useContact, useHeldLeadCard, useLeadAccident, useLeadCard } from "@/lib/queries";
import {
  ACCIDENT_CARD_ID, HINT_SHOWN_MS, SAME_PHONE_CARD_ID, SEEN_BEFORE, asSibling, recordsText, seenBefore,
  showsHint, type SeenBefore,
} from "@/lib/seen-before";
import type { LeadAccident, LeadCard } from "@/lib/types";
import { cn } from "@/lib/utils";
import { RAIL_CELL } from "@/softphone/rail";
import { PHONE_COMPANION } from "@/softphone/voicemail-suggestion";

/**
 * "Seen before" (ACL-409): a lead's history where a rep first meets the lead. The other records with
 * its phone, with its e-mail, and the people in its accident were on the record already, low in its
 * left column, where a rep who does not scroll missed them; now they are said at the top of the
 * record (`SeenBeforeTag`), on the dialer's offer before Dial (`SeenBeforeLine`), and listed in a
 * panel from the phone's rail (`SeenBeforeRailButton`). Shown, never decided: everything here reads,
 * and the panel's marks are the call card's, without buttons (`AccidentMark compact`): saying how two
 * records relate stays on the record's cards.
 *
 * The rail is the dock's and the record is a route, so which lead the rail is about is a module store,
 * as the Phone page's panes are (`call-panes.tsx`): the record page says which lead it shows, once per
 * visit (`useSeenBeforePage`), and the tag or the offer's View asks for a lead (`showSeenBefore`).
 * On the record its lists are the record's own queries (`useContact`, `useLeadAccident`), so the rail
 * reads nothing the page has not. A lead the offer's View asks about is read once, under the phone's
 * own key for its card (`useLeadCard(id, null)`, the one the Phone page holds for an offered lead), and
 * never polled; the offer's line itself reads only the accident's route (`SeenBeforeLine`).
 *
 * The panel opens only when somebody asks: the rail button, the tag, the offer's View. What happens
 * by itself, once per visit to a record that has something to show, is the rail button's own tooltip
 * ("Seen before: 2 other records · Do not call"), level with the button, so the rep sees there is
 * something to look at without a list over the page they came to read. Never while the phone is
 * ringing, open or suggesting a voicemail (`showsHint`): decided once, when the lists are in, as the
 * phone opens itself only when a call arrives. A tooltip takes no focus. It goes when the rep presses
 * the button, clicks anywhere else, a call rings, the phone opens, or after `HINT_SHOWN_MS`, as the
 * voicemail suggestion's choices that opened by themselves do. The panel closes with a click anywhere
 * else, and steps out of the way for a ringing call or a voicemail suggestion, as the day's calls do.
 */

interface Store {
  /** The record page on screen, and which visit of it this is: a new one each time it shows a lead. */
  page: { leadId: string; visit: number } | null;
  /** A lead somebody asked to see from outside the rail (the tag, the offer's View), until the panel
   *  closes. A new object for every ask, so asking again opens it again. */
  asked: { leadId: string } | null;
  /** How many rails are drawn: none in a narrow window, or for somebody with no phone. */
  rails: number;
}

const EMPTY: Store = { page: null, asked: null, rails: 0 };
let store: Store = EMPTY;
let visits = 0;
const watchers = new Set<() => void>();

const subscribe = (notify: () => void) => {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
};

function update(next: Partial<Store>): void {
  store = { ...store, ...next };
  for (const notify of watchers) notify();
}

function useStore(): Store {
  return React.useSyncExternalStore(subscribe, () => store, () => EMPTY);
}

/** The record page says which lead it shows, for as long as it shows it: each time is a visit. */
export function useSeenBeforePage(leadId: string | undefined): void {
  React.useEffect(() => {
    if (!leadId) return;
    const mine = { leadId, visit: ++visits };
    update({ page: mine });
    return () => {
      if (store.page === mine) update({ page: null });
    };
  }, [leadId]);
}

/** Opens the panel on this lead's history: the record's tag, and View on the dialer's offer. */
export function showSeenBefore(leadId: string): void {
  update({ asked: { leadId } });
}

/** Whether a rail is drawn to open the panel in. */
export function useSeenBeforeRail(): boolean {
  return useStore().rails > 0;
}

/** Forgets the page, the ask and the rails. For tests. */
export function resetSeenBefore(): void {
  visits = 0;
  update({ ...EMPTY });
}

/** On what opens the panel from outside the rail (the tag, View): a press on it while the panel is
 *  open is not a click elsewhere that closes it, only for the press to open it again. */
const OPENER = "data-seen-before-opener";

/** The popover's box, as the day's calls have it. */
const CONTENT = "flex w-80 flex-col p-0 max-h-[var(--radix-popover-content-available-height)]";

/**
 * The lead's two lists, and what they add up to. The record on screen is read as its page reads it;
 * any other lead, one the offer's View asked about, by the phone's own card read: on the Phone page
 * that is the card it already holds for the offered lead, and nowhere is it polled.
 */
function useHistory(leadId: string | null, { onRecord }: { onRecord: boolean }) {
  const record = useContact(onRecord && leadId !== null ? leadId : undefined);
  const phone = useLeadCard(onRecord ? null : leadId, null);
  const card = onRecord ? record : phone;
  const accident = useLeadAccident(leadId);
  return {
    card: card.data,
    accident: accident.data,
    summary: seenBefore(card.data?.siblings, accident.data),
    // Both answered, either way: an older core-api without the accident's route answers 404.
    loaded: leadId !== null && (card.isSuccess || card.isError) && (accident.isSuccess || accident.isError),
  };
}

/**
 * The rail's button: there while the record on screen, or the lead somebody asked about, has a
 * history to show, with the count of other records over its icon (My calls has the clock). A popover
 * of its own, built as the day's calls are: its own root, the companion mark on its cell and its
 * content so the phone does not close for a press on them, and beside whatever else the rail has open
 * (`railOffset`), never over the call.
 */
export function SeenBeforeRailButton({
  phoneOpen, suggestionOpen, ringing,
}: {
  /** The phone's own popover is open. */
  phoneOpen: boolean;
  /** A voicemail is suggested and its choices are open (`VoicemailRailButton`). */
  suggestionOpen: boolean;
  /** A call is ringing this phone. */
  ringing: boolean;
}) {
  React.useEffect(() => {
    update({ rails: store.rails + 1 });
    return () => update({ rails: Math.max(0, store.rails - 1) });
  }, []);
  const { page, asked } = useStore();
  const leadId = asked?.leadId ?? page?.leadId ?? null;
  const { card, accident, summary, loaded } = useHistory(leadId, { onRecord: leadId === page?.leadId });
  const [open, setOpen] = React.useState(false);
  const [tipOpen, setTipOpen] = React.useState(false);
  // The tooltip, shown by itself once per visit.
  const [hint, setHint] = React.useState(false);
  // The visit whose tooltip was decided, so it is decided once.
  const decided = React.useRef<number | null>(null);
  const visit = page?.visit ?? null;
  const phoneBusy = phoneOpen || ringing || suggestionOpen;

  const close = React.useCallback(() => {
    setOpen(false);
    if (store.asked) update({ asked: null });
  }, []);

  // A new visit starts with nothing shown, unless somebody asked for a lead from outside it.
  React.useEffect(() => {
    setHint(false);
    if (!store.asked) setOpen(false);
  }, [visit]);

  // Asked for, by the tag or the offer's View: open on that lead, as the rep's own press does.
  React.useEffect(() => {
    if (!asked) return;
    setHint(false);
    setOpen(true);
  }, [asked]);

  // The tooltip by itself, once per visit, once the record's lists are in.
  const count = summary?.count ?? 0;
  React.useEffect(() => {
    if (visit === null || decided.current === visit || store.asked) return;
    const shows = showsHint({ settled: loaded, count, phoneBusy });
    if (shows === null) return;
    decided.current = visit;
    setHint(shows);
  }, [visit, loaded, count, phoneBusy]);

  // It goes by itself after a while, and at once for the phone.
  React.useEffect(() => {
    if (!hint) return;
    const timer = setTimeout(() => setHint(false), HINT_SHOWN_MS);
    return () => clearTimeout(timer);
  }, [hint]);
  React.useEffect(() => {
    if (phoneBusy) setHint(false);
  }, [phoneBusy]);

  // The panel out of the way for a call ringing or a voicemail suggested, as the day's calls are.
  React.useEffect(() => {
    if (ringing || suggestionOpen) close();
  }, [ringing, suggestionOpen, close]);

  // Nothing left to show.
  React.useEffect(() => {
    if (open && loaded && !summary) close();
  }, [open, loaded, summary, close]);

  if (leadId === null || !summary) return null;
  // What the tooltip says, by itself or under the pointer, and the button's name.
  const name = [`${SEEN_BEFORE}: ${recordsText(summary.count)}`, summary.fact?.text].filter(Boolean).join(" · ");
  const companion = { [PHONE_COMPANION]: "" };
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) return close();
        setHint(false);
        setOpen(true);
      }}
    >
      <PopoverAnchor asChild>
        <div className={RAIL_CELL} {...companion}>
          <Tooltip
            content={name}
            side="left"
            open={(tipOpen || hint) && !open}
            onOpenChange={(next) => {
              setTipOpen(next);
              // A press on the button, a click elsewhere, Escape, the pointer leaving: it has been seen.
              if (!next) setHint(false);
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                // Radix opens the tooltip from a focus handler that stands down for a prevented event.
                onFocus={(e) => e.preventDefault()}
                aria-label={name}
                className={cn(
                  "relative flex size-9 items-center justify-center rounded-full transition-colors",
                  open
                    ? "bg-frame-hover text-text"
                    : "text-text-muted hover:bg-frame-hover hover:text-text",
                )}
              >
                <Layers className="size-[18px]" aria-hidden />
                <span
                  className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-9 px-1 text-2xs font-semibold leading-none text-white ring-2 ring-frame tabular"
                  aria-hidden
                >
                  {summary.count > 9 ? "9+" : summary.count}
                </span>
              </button>
            </PopoverTrigger>
          </Tooltip>
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={railOffset({ phoneOpen, suggestionOpen })}
        aria-label={SEEN_BEFORE}
        className={CONTENT}
        {...companion}
        // The keyboard stays where the rep has it: back to the button only from inside the panel.
        onCloseAutoFocus={(e) => {
          const active = document.activeElement;
          if (active !== null && active !== document.body) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (e.target instanceof Element && e.target.closest(`[${OPENER}]`)) e.preventDefault();
        }}
      >
        <SeenBeforePanel
          leadId={leadId}
          card={card}
          accident={accident}
          summary={summary}
          onRecord={page?.leadId === leadId}
          onNavigate={close}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The panel: whose history and the fact that matters most, then the records by why they are listed,
 * each drawn as the record's Same phone card draws one (a restricted record by its firm and stage
 * alone), with the call card's mark under it. Following a record closes it.
 */
function SeenBeforePanel({
  leadId, card, accident, summary, onRecord, onNavigate,
}: {
  leadId: string;
  card: LeadCard | undefined;
  accident: LeadAccident | undefined;
  summary: SeenBefore;
  /** The record page on screen is this lead's: it needs no way there. */
  onRecord: boolean;
  onNavigate: () => void;
}) {
  const { byEmail, linked } = accidentSections(accident);
  const marks = new Map((accident?.records ?? []).map((r) => [r.id, r]));
  const mark = (id: string) => <AccidentMark leadId={leadId} record={marks.get(id)} compact />;
  const siblings = card?.siblings ?? [];
  const name = card ? contactName(card) : null;
  return (
    <div
      className="flex min-h-0 flex-col"
      onClick={(e) => {
        if (e.target instanceof Element && e.target.closest("a")) onNavigate();
      }}
    >
      {/* The header the day's calls and the missed calls have: a title, one line under it, and here
          the fact as the record's other chips show one. */}
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-text">{SEEN_BEFORE}</h2>
          {summary.fact && (
            <Badge tone={summary.fact.tone} size="sm" className="min-w-0 max-w-[60%]" title={summary.fact.text}>
              <span className="truncate">{summary.fact.text}</span>
            </Badge>
          )}
        </div>
        <p className="truncate text-xs text-text-muted">{[name, recordsText(summary.count)].filter(Boolean).join(" · ")}</p>
      </div>
      <div className="min-h-0 space-y-3 overflow-y-auto px-4 py-3 scrollbar-subtle">
        {siblings.length > 0 && (
          <PanelSection title="Same phone">
            <SamePhoneRecords siblings={siblings} phone={card?.phone ?? ""} compact footer={(s) => mark(s.id)} />
          </PanelSection>
        )}
        {byEmail.length > 0 && (
          <PanelSection title="Same email">
            <p className="flex items-start gap-1.5 text-2xs text-text-muted">
              <Info className="mt-px size-3 shrink-0" aria-hidden />
              <span>{SAME_EMAIL_CAVEAT}</span>
            </p>
            <SamePhoneRecords
              siblings={byEmail.map(asSibling)}
              phone=""
              compact
              caveat={false}
              label="Other records with this e-mail"
              footer={(s) => mark(s.id)}
            />
          </PanelSection>
        )}
        {linked.length > 0 && (
          <PanelSection title="In this accident">
            <SamePhoneRecords
              siblings={linked.map(asSibling)}
              phone=""
              compact
              caveat={false}
              label="Others in this accident"
              footer={(s) => mark(s.id)}
            />
          </PanelSection>
        )}
      </div>
      {!onRecord && (
        <div className="shrink-0 border-t border-border px-4 py-2 text-xs">
          <Link to={`/contacts/${leadId}`} className="font-medium text-accent-11 hover:underline">
            Open record
          </Link>
        </div>
      )}
    </div>
  );
}

function PanelSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title}>
      <p className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-subtle">{title}</p>
      {children}
    </section>
  );
}

/**
 * The record's tag, at the top: how many other records, and the fact that matters most. It opens the
 * panel; where there is no rail (a narrow window, or no phone) it scrolls to the record's own Same
 * phone card, else its Accident card, which hold the same lists. Nothing for a record with none.
 */
export function SeenBeforeTag({ contact }: { contact: LeadCard }) {
  const accident = useLeadAccident(contact.id, { poll: true });
  const rail = useSeenBeforeRail();
  const summary = seenBefore(contact.siblings, accident.data);
  if (!summary) return null;
  const words = [SEEN_BEFORE, recordsText(summary.count), summary.fact?.text].filter(Boolean).join(" · ");
  const show = () => {
    if (rail) {
      showSeenBefore(contact.id);
      return;
    }
    const card = document.getElementById(SAME_PHONE_CARD_ID) ?? document.getElementById(ACCIDENT_CARD_ID);
    card?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button
      type="button"
      onClick={show}
      title={words}
      {...{ [OPENER]: "" }}
      className="min-w-0 max-w-full rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8"
    >
      <Badge tone={summary.fact?.tone ?? "neutral"} className="max-w-full cursor-pointer hover:brightness-95">
        <Layers className="size-3 shrink-0" aria-hidden />
        <span className="truncate">{words}</span>
      </Badge>
    </button>
  );
}

/**
 * The dialer offer's line before Dial: how many other records the lead has and the fact that matters
 * most, with View, which opens the panel beside the phone. The offer never waits for it: the line is
 * drawn when the lead's lists come in, and nothing for a lead with none. In a narrow window, with no
 * rail to open the panel in, View is the way to the record.
 *
 * It asks only the accident's route, once and with no poll: that lists the records with the phone
 * too, with their stage and do-not-call. The card, which adds a turned-down record's reason, is taken
 * only where the phone already holds it (the Phone page, `useHeldLeadCard`). An offer used to read
 * nothing, and `GET /leads/{id}` is core-api's costliest read on a dialing floor (ACL-390).
 */
export function SeenBeforeLine({ leadId }: { leadId: string }) {
  const held = useHeldLeadCard(leadId);
  const accident = useLeadAccident(leadId);
  const summary = seenBefore(held.data?.siblings, accident.data);
  const rail = useSeenBeforeRail();
  if (!summary) return null;
  // The fact under the count: in the phone's 320px the two did not fit in a row, and the count, cut
  // to "2 ot…", was the half that went.
  return (
    <div className="rounded-lg border border-border px-3 py-1.5 text-xs">
      <div className="flex items-center gap-2">
        <Layers className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium text-text">{SEEN_BEFORE}</span>
          <span className="text-text-muted"> · {recordsText(summary.count)}</span>
        </span>
        {rail ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 shrink-0 px-2"
            {...{ [OPENER]: "" }}
            onClick={() => showSeenBefore(leadId)}
          >
            View
          </Button>
        ) : (
          <Link to={`/contacts/${leadId}`} className="shrink-0 font-medium text-accent-11 hover:underline">
            View
          </Link>
        )}
      </div>
      {summary.fact && (
        <Badge tone={summary.fact.tone} size="sm" className="mb-0.5 ml-[1.375rem] max-w-[calc(100%-1.375rem)]" title={summary.fact.text}>
          <span className="truncate">{summary.fact.text}</span>
        </Badge>
      )}
    </div>
  );
}
